import type { ServiceEntry } from '../services/types';
import { ChevronDownIcon, CheckIcon, ExternalLinkIcon } from '../icons';
import { IconByName } from '../icons/registry';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface ServiceSwitcherProps {
  services: ServiceEntry[];
  activeServiceId?: string | null;
  /** The display name of the currently-active subsystem. */
  currentLabel: string;
}

/**
 * Left-aligned dropdown that shows the current subsystem name and opens a
 * list of all configured services. Clicking a service navigates via full
 * page load (hard jump) — shell and subsystems are independent SPAs.
 *
 * Uses shadcn DropdownMenu wrappers — focus management, escape,
 * click-outside, roving tabindex, ARIA all handled by Radix underneath.
 */
export function ServiceSwitcher({ services, activeServiceId, currentLabel }: ServiceSwitcherProps) {
  const activeService = activeServiceId ? services.find((s) => s.id === activeServiceId) : undefined;
  const activeIcon =
    activeService?.icon ??
    (activeService?.iconName ? (
      <IconByName name={activeService.iconName} className="w-4 h-4" />
    ) : null);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2 py-1.5 -ml-1 rounded-md text-sm text-foreground hover:bg-muted transition-colors max-w-[16rem] data-[state=open]:bg-muted"
        >
          {activeIcon && (
            <span className="w-4 h-4 shrink-0 text-muted-foreground">{activeIcon}</span>
          )}
          <span className="truncate">{currentLabel}</span>
          <ChevronDownIcon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" sideOffset={4} className="w-64">
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Switch service
        </DropdownMenuLabel>

        {services.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            No services configured. See <code>VITE_SERVICES</code>.
          </div>
        )}

        {services.map((svc) => {
          const isActive = svc.id === activeServiceId;
          const isExternal = /^https?:\/\//.test(svc.url);
          const iconNode =
            svc.icon ?? (svc.iconName ? <IconByName name={svc.iconName} className="w-4 h-4" /> : null);

          if (svc.comingSoon) {
            return (
              <DropdownMenuItem key={svc.id} disabled>
                {iconNode && <span className="w-4 h-4 shrink-0 opacity-60">{iconNode}</span>}
                <span className="truncate flex-1">{svc.name}</span>
                <span className="text-[10px] uppercase tracking-wide px-1 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400">
                  Soon
                </span>
              </DropdownMenuItem>
            );
          }

          return (
            <DropdownMenuItem key={svc.id} asChild>
              <a
                href={svc.url}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noreferrer' : undefined}
                aria-current={isActive ? 'page' : undefined}
                className={
                  isActive
                    ? 'bg-primary/10 text-primary focus:bg-primary/15 focus:text-primary'
                    : ''
                }
              >
                {iconNode && <span className="w-4 h-4 shrink-0">{iconNode}</span>}
                <span className="truncate flex-1">{svc.name}</span>
                {isActive && <CheckIcon className="w-4 h-4 text-primary" />}
                {isExternal && !isActive && <ExternalLinkIcon className="w-3 h-3 opacity-60" />}
              </a>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
